import {
  setCurrentProductEmail,
  withProductEmail,
} from './productEventEmail';

export const LOGIN_FAILURE_REASONS = [
  'invalid_credentials',
  'expired_link',
  'network',
  'unknown',
] as const;

export type LoginFailureReason = (typeof LOGIN_FAILURE_REASONS)[number];

export interface AnalyticsCaptureClient {
  capture: (event: string, properties?: Record<string, unknown>) => void;
}

export interface AnalyticsIdentifyClient {
  identify: (
    distinctId: string,
    properties?: Record<string, unknown>,
  ) => void;
}

/** Person properties for posthog.identify. `email` is the address string. */
export function posthogPersonProperties(user: {
  id: string;
  email?: string | null;
  created_at?: string | null;
  last_sign_in_at?: string | null;
  app_metadata?: { provider?: string } | null;
  user_metadata?: Record<string, unknown> | null;
}): Record<string, unknown> {
  const metadata = user.user_metadata || {};
  return {
    email: user.email,
    user_id: user.id,
    provider: user.app_metadata?.provider || 'email',
    created_at: user.created_at,
    last_sign_in: user.last_sign_in_at,
    tier: metadata.tier || 'free',
    ...metadata,
  };
}

function signalText(error: unknown): string {
  if (error == null) {
    return '';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'number') {
    return String(error);
  }
  if (error instanceof Error) {
    const extra = error as Error & { code?: unknown; status?: unknown };
    return [extra.name, extra.code, extra.status, extra.message]
      .filter((part) => part != null && part !== '')
      .join(' ');
  }
  if (typeof error === 'object') {
    const record = error as Record<string, unknown>;
    return [
      record.name,
      record.code,
      record.error,
      record.error_code,
      record.error_description,
      record.message,
      record.status,
    ]
      .filter((part) => part != null && part !== '')
      .join(' ');
  }
  return '';
}

export function classifyLoginFailure(error: unknown): LoginFailureReason {
  const text = signalText(error).toLowerCase();
  if (
    text.includes('failed to fetch') ||
    text.includes('network') ||
    text.includes('timeout') ||
    text.includes('econn') ||
    text.includes('load failed') ||
    text.includes('networkerror') ||
    /\bstatus[\s:]*0\b/.test(text)
  ) {
    return 'network';
  }
  if (
    text.includes('otp_expired') ||
    text.includes('expired') ||
    text.includes('link is invalid')
  ) {
    return 'expired_link';
  }
  if (
    text.includes('invalid_credentials') ||
    text.includes('invalid login') ||
    text.includes('invalid_grant') ||
    text.includes('invalid grant') ||
    text.includes('wrong password') ||
    text.includes('invalid email or password')
  ) {
    return 'invalid_credentials';
  }
  return 'unknown';
}

/** Product event for a failed sign-in. Reason code only, never email or tokens. */
export function captureLoginFailed(
  posthog: AnalyticsCaptureClient | null | undefined,
  error: unknown,
): void {
  if (!posthog) {
    return;
  }
  posthog.capture('login_failed', { reason: classifyLoginFailure(error) });
}

/** Distinct id is the internal user id. Optional properties may include email. */
export function identifyByUserId(
  posthog: AnalyticsIdentifyClient | null | undefined,
  userId: string | null | undefined,
  properties?: Record<string, unknown>,
): void {
  if (!posthog || !userId) {
    return;
  }
  if (properties) {
    if (typeof properties.email === 'string' || properties.email == null) {
      setCurrentProductEmail(
        typeof properties.email === 'string' ? properties.email : null,
      );
    }
    posthog.identify(userId, properties);
    return;
  }
  posthog.identify(userId);
}

/**
 * Fires onboarding_completed once per user. A repeat call after the flag is
 * stored does not send another event.
 */
export function captureOnboardingCompleted(
  posthog: AnalyticsCaptureClient | null | undefined,
  userId: string | null | undefined,
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = typeof window !==
    'undefined'
    ? window.localStorage
    : null,
  email?: string | null,
): void {
  if (userId && storage) {
    const key = `onboarding_completed_${userId}`;
    const already = storage.getItem(key) === 'true';
    storage.setItem(key, 'true');
    if (already) {
      return;
    }
  }
  posthog?.capture('onboarding_completed', withProductEmail({}, email));
}

export function readAuthUrlFailure(
  locationLike?: { search: string; hash: string },
): { error: string; error_code: string; error_description: string } | null {
  const loc =
    locationLike ?? (typeof window !== 'undefined' ? window.location : null);
  if (!loc) {
    return null;
  }
  const search = new URLSearchParams(
    loc.search.startsWith('?') ? loc.search.slice(1) : loc.search,
  );
  const hash = new URLSearchParams(
    loc.hash.startsWith('#') ? loc.hash.slice(1) : loc.hash,
  );
  const error = search.get('error') || hash.get('error') || '';
  const errorCode = search.get('error_code') || hash.get('error_code') || '';
  const errorDescription =
    search.get('error_description') || hash.get('error_description') || '';
  if (!error && !errorCode && !errorDescription) {
    return null;
  }
  return {
    error,
    error_code: errorCode,
    error_description: errorDescription,
  };
}
